import React, { useCallback, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { getSearchValue } from "../../../services/userServices";
import { rosterConfigAction } from "../../../Store/RosterConfig";
import { classRequestActions } from "Store/ClassRequest";
import BackdropComponent from "../../Spinner/Spinner";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

// ── Urbanist font applied via sx fontFamily throughout ──────────────────────
const FONT = "Urbanist, Verdana, Arial, sans-serif";
const BRAND_BLUE = "#3299FF";

// Label map — keeps JSX lean
const LABEL_MAP = {
  trainerGlobal: "Search Trainer",
  trainerSingle: "Search Trainer",
  ogGlobal: "Search OG",
  ogSingle: "Search OG",
  tpcGlobal: "Search Ops Team Lead",
  tpcSingle: "Search Ops Team Lead",
  nstGlobal: "Search Nesting Team Lead",
  nstSingle: "Search Nesting Team Lead",
  fdoGlobal: "Search FDO",
  fdoSingle: "Search FDO",
  trainer: "Search Trainer",
  og: "Search OG",
  tpc: "Search TPC Host",
  requestedFor: "Requested For",
  waveTrainer: "Wave Trainer",
  manager: "Manager",
  OpsApproval: "Ops Approval",
};

const SearchModal = (props) => {
  const { t } = useTranslation(["class"]);
  const dispatch = useDispatch();

  const [loading, setLoader] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [minCharError, setMinCharError] = useState(false);

  // ── Search ────────────────────────────────────────────────────────────────
  const searchHandler = useCallback(async () => {
    if (searchValue.length > 2) {
      setMinCharError(false);
      setLoader(true);
      const res = await getSearchValue(searchValue);
      if (res.message === "Success") setSearchResult(res.data);
      setLoader(false);
    } else {
      setMinCharError(true);
    }
  }, [searchValue]);

  // ── Dispatch on item select ───────────────────────────────────────────────
  const handleDispatch = useCallback((result) => {
    const { type, onClose } = props;
    if (type === "trainer") {
      dispatch(rosterConfigAction.traineeName(result.Name));
      dispatch(rosterConfigAction.traineeEmail(result.Email));
      dispatch(rosterConfigAction.updateTrainerOracle(result.OracleId));
      onClose();
    } else if (type === "og") {
      dispatch(rosterConfigAction.ogName(result.Name));
      dispatch(rosterConfigAction.ogEmail(result.Email));
      dispatch(rosterConfigAction.updateOgOracle(result.OracleId));
      onClose();
    } else if (type === "tpc") {
      dispatch(rosterConfigAction.tpcHostName(result.Name));
      dispatch(rosterConfigAction.tpcHostEmail(result.Email));
      dispatch(rosterConfigAction.updateTcpOracle(result.OracleId));
      onClose();
    } else if (type === "OpsApproval") {
      dispatch(classRequestActions.updateOpsApprovalOracleId(result.OracleId));
      dispatch(classRequestActions.updateOpsApprovalName(result.Name));
      onClose();
    } else {
      // All Global / Single / requestedFor / waveTrainer / manager types
      onClose(result, type);
    }
  }, []);

  const label = LABEL_MAP[props.type] ?? "Search";

  return (
    <>
      {loading && <BackdropComponent />}

      {/* ── Modal shell ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          fontFamily: FONT,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Search input */}
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          {/* Minimal header: label left, close right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "13px",
                color: "#555",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
            <IconButton
              size="small"
              onClick={props.onClose}
              sx={{
                color: "#9e9e9e",
                "&:hover": { color: BRAND_BLUE, backgroundColor: "#e8f2ff" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={t("Search")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") searchHandler();
            }}
            error={minCharError}
            helperText={
              minCharError ? "Please enter at least 3 characters" : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={searchHandler}
                    sx={{
                      color: "#fff",
                      backgroundColor: BRAND_BLUE,
                      borderRadius: "6px",
                      p: "5px",
                      "&:hover": { backgroundColor: "#2878CC" },
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                fontFamily: FONT,
                fontSize: "13px",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#c5d8f0" },
                "&:hover fieldset": { borderColor: BRAND_BLUE },
                "&.Mui-focused fieldset": { borderColor: BRAND_BLUE },
              },
            }}
            FormHelperTextProps={{
              sx: { fontFamily: FONT, fontSize: "11px", color: "#d32f2f" },
            }}
          />
        </Box>

        {/* Results list */}
        {useMemo(
          () => (
            <List
              disablePadding
              sx={{
                maxHeight: 280,
                overflowY: "auto",
                px: 1,
                py: 0.5,
                // slim scrollbar
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c5d8f0",
                  borderRadius: 4,
                },
              }}
            >
              {searchResult.length === 0 ? (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "13px",
                    color: "#9e9e9e",
                    textAlign: "center",
                    py: 3,
                  }}
                >
                  No results to display
                </Typography>
              ) : (
                searchResult.map((result, i) => (
                  <ListItemButton
                    key={i}
                    onClick={() => handleDispatch(result)}
                    sx={{
                      borderRadius: "6px",
                      mb: 0.5,
                      fontFamily: FONT,
                      "&:hover": {
                        backgroundColor: "#e8f2ff",
                        "& .result-primary": { color: BRAND_BLUE },
                      },
                    }}
                  >
                    <ListItemText
                      primary={result.Name}
                      secondary={result.Email}
                      primaryTypographyProps={{
                        className: "result-primary",
                        sx: {
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#212121",
                          transition: "color 0.15s",
                        },
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontFamily: FONT,
                          fontSize: "11px",
                          color: "#757575",
                        },
                      }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          ),
          [searchResult],
        )}
      </Box>
    </>
  );
};

export default SearchModal;
